import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Sparkles, BookOpen, Trash2, RefreshCw, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SESSION_KEY = "emergent_aid_session_id";

const QUICK_PROMPTS = [
  "Geef me een korte briefing over het platform vandaag.",
  "Welke openstaande voorstellen verdienen aandacht?",
  "Wat zegt de recente feedback over de gebruikservaring?",
  "Welke risico's zie je in de huidige groeicurve?",
];

export const EmergentAid = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat"); // chat | notes
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(localStorage.getItem(SESSION_KEY) || "");
  const [snapshot, setSnapshot] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const endRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/aid/snapshot`);
      setSnapshot(r.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Aid] snapshot failed:", err?.message);
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoadingNotes(true);
    try {
      const r = await axios.get(`${API}/aid/notes?limit=30`);
      setNotes(r.data.notes || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Aid] notes failed:", err?.message);
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSnapshot();
      fetchNotes();
    }
  }, [open, fetchSnapshot, fetchNotes]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: msg, ts: Date.now() }]);
    setInput("");
    try {
      const res = await axios.post(`${API}/aid/chat`, { message: msg, session_id: sessionId || undefined });
      const sid = res.data.session_id;
      if (sid && sid !== sessionId) {
        setSessionId(sid);
        localStorage.setItem(SESSION_KEY, sid);
      }
      setMessages((m) => [...m, { role: "aid", text: res.data.reply, ts: Date.now() }]);
      if (res.data.snapshot) setSnapshot(res.data.snapshot);
    } catch (err) {
      setMessages((m) => [...m, { role: "aid", text: "Er ging iets mis. Probeer het opnieuw.", ts: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  const saveNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/aid/notes`, { text: newNote.trim() });
      setNewNote("");
      toast.success("Notitie opgeslagen");
      fetchNotes();
    } catch {
      toast.error("Notitie opslaan mislukt");
    }
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/aid/notes/${id}`);
      toast.success("Notitie verwijderd");
      fetchNotes();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const resetSession = () => {
    setMessages([]);
    setSessionId("");
    localStorage.removeItem(SESSION_KEY);
    toast.success("Nieuwe sessie gestart");
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full bg-gradient-to-r from-[#002FA7] to-[#7000FF] text-white p-4 flex items-center justify-between hover:opacity-90 transition-opacity"
        data-testid="open-emergent-aid"
      >
        <span className="flex items-center gap-2 font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
          <Sparkles className="w-4 h-4" />
          Emergent Aid — Open assistent
        </span>
        <span className="text-[10px] font-mono opacity-80">FOUNDER ONLY</span>
      </button>
    );
  }

  return (
    <div className="bg-white border-2 border-[#002FA7] shadow-lg" data-testid="emergent-aid-panel">
      <div className="bg-gradient-to-r from-[#002FA7] to-[#7000FF] text-white px-4 py-3 flex items-center justify-between">
        <span className="flex items-center gap-2 font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
          <Sparkles className="w-4 h-4" />
          Emergent Aid
        </span>
        <div className="flex items-center gap-2">
          <button onClick={resetSession} className="text-[10px] font-mono opacity-80 hover:opacity-100" title="Nieuwe sessie" data-testid="aid-reset-session">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setOpen(false)} className="opacity-80 hover:opacity-100" title="Sluit" data-testid="aid-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { id: "chat", label: "Chat", icon: Sparkles },
          { id: "notes", label: "Notities", icon: BookOpen },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              tab === t.id ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`aid-tab-${t.id}`}
          >
            <t.icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Snapshot */}
      {snapshot && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 text-[11px] text-gray-600 grid grid-cols-3 gap-2" data-testid="aid-snapshot">
          <div>
            <p className="font-mono text-gray-400 text-[9px]">DEBATTEN</p>
            <p className="font-bold">{snapshot.debates_completed || 0}/{snapshot.debates_total || 0}</p>
          </div>
          <div>
            <p className="font-mono text-gray-400 text-[9px]">VOORSTELLEN</p>
            <p className="font-bold">{snapshot.proposals_pending || 0}</p>
          </div>
          <div>
            <p className="font-mono text-gray-400 text-[9px]">DECISIONS</p>
            <p className="font-bold">{snapshot.pending_decisions || 0}</p>
          </div>
        </div>
      )}

      {tab === "chat" ? (
        <>
          {/* Messages */}
          <div className="max-h-[360px] min-h-[200px] overflow-y-auto p-3 space-y-3" data-testid="aid-messages">
            {messages.length === 0 && (
              <div className="text-center text-xs text-gray-500 py-4 space-y-3">
                <p>Stel een vraag of kies een snelle prompt:</p>
                <div className="flex flex-col gap-1.5">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="text-left text-[11px] px-3 py-2 bg-gray-50 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
                      data-testid="aid-quick-prompt"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={`${m.ts}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 text-xs whitespace-pre-wrap ${
                    m.role === "user" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-3 py-2 text-xs flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-3 h-3 animate-spin" /> Aid denkt na...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-2 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Stel een vraag aan Emergent Aid..."
              disabled={sending}
              className="flex-1 text-xs border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-[#002FA7] disabled:opacity-50"
              data-testid="aid-input"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-3 py-1.5 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001A6E] disabled:opacity-50 flex items-center gap-1"
              data-testid="aid-send"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </button>
          </div>
        </>
      ) : (
        <div className="p-3 space-y-3" data-testid="aid-notes-tab">
          <div className="flex gap-2">
            <input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveNote()}
              placeholder="Korte notitie voor later..."
              className="flex-1 text-xs border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-[#002FA7]"
              data-testid="aid-note-input"
            />
            <button
              onClick={saveNote}
              disabled={!newNote.trim()}
              className="px-3 py-1.5 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#001A6E] disabled:opacity-50"
              data-testid="aid-note-save"
            >
              Bewaar
            </button>
          </div>
          {loadingNotes ? (
            <div className="text-center py-4 text-xs text-gray-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : notes.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">Nog geen notities.</p>
          ) : (
            <div className="space-y-2 max-h-[340px] overflow-y-auto">
              {notes.map((n) => (
                <div key={n.note_id} className="border border-gray-200 p-2 flex items-start justify-between gap-2" data-testid={`aid-note-${n.note_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs whitespace-pre-wrap break-words">{n.text}</p>
                    <p className="text-[10px] font-mono text-gray-400 mt-1">{n.note_id} — {n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                  </div>
                  <button onClick={() => deleteNote(n.note_id)} className="text-gray-400 hover:text-red-600" title="Verwijder" data-testid={`aid-note-delete-${n.note_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
